'use client';
import React, { useRef } from 'react';
import { m, useInView } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { components } from './PortableTextComponents';
import { fadeUp, fadeUpLeft } from '@/assets/framer/animations';
import { sectionPadding } from '../utils/utils';
import { IKButton } from '../shared/material';
import { ArrowRight } from '@phosphor-icons/react';
import { MenuItemLink } from '@/types/types';
import { useRelativeCoordinates } from '@/hooks/useRelativeCoordinates';

function SVGPatterns({
  mousePosition,
  isInView,
}: {
  className?: string;
  mousePosition: any;
  isInView: boolean;
}) {
  return (
    <div>
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        className='translate absolute left-0 top-24 hidden translate-x-24 md:block'
        xmlns='http://www.w3.org/2000/svg'
        width='70'
        height='71'
        viewBox='0 0 70 71'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 96 + 96 : 96,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M28.1887 28.4598C28.4904 28.4604 28.7321 28.2193 28.7327 27.9174L28.7576 15.1368L41.9694 15.1625L41.9445 27.9432C41.9439 28.2451 42.1847 28.4871 42.4864 28.4877L54.7933 28.5117L54.7676 41.6747L41.9982 41.6498C41.6966 41.6492 41.4548 41.8903 41.4542 42.1922L41.4287 55.2949L29.0012 55.2706L29.0266 42.2284C29.0272 41.9264 28.7864 41.6845 28.4847 41.6839L15.3735 41.6583L15.3993 28.4349L28.1686 28.4598L28.1887 28.4598ZM1.19494 42.4156L14.3061 42.4412L14.2799 55.886C14.2793 56.1879 14.5202 56.4299 14.8219 56.4305L28.1543 56.4565L28.1289 69.4988C28.1283 69.8007 28.3691 70.0427 28.6708 70.0433L42.345 70.0699C42.6466 70.0705 42.8885 69.8294 42.8891 69.5275L42.9146 56.4047L55.684 56.4296C55.9856 56.4302 56.2274 56.1892 56.228 55.8873L56.2542 42.4424L69.0236 42.4673C69.3252 42.4679 69.567 42.2268 69.5676 41.9249L69.5942 28.2587C69.5948 27.9568 69.354 27.7148 69.0523 27.7142L56.7455 27.6902L56.7716 14.3058C56.7721 14.0039 56.5313 13.7619 56.2296 13.7613L42.7967 13.7351L42.8216 0.934371C42.8222 0.632466 42.5814 0.390472 42.2797 0.389884L28.6256 0.363277C28.3239 0.362689 28.0822 0.603743 28.0816 0.905648L28.0567 13.6662L14.6237 13.64C14.3221 13.6394 14.0803 13.8804 14.0797 14.1824L14.0535 27.6272L1.30426 27.6023C1.00262 27.6017 0.760799 27.8428 0.760211 28.1447L0.73358 41.8109C0.732991 42.1128 0.973871 42.3548 1.27551 42.3554'
          fill='#51504D'
        />
      </m.svg>
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        className='absolute -left-16 top-36 hidden md:block'
        xmlns='http://www.w3.org/2000/svg'
        width='150'
        height='150'
        viewBox='-66 0 147 150'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 50 - 10 : 0,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M-39.7367 80.6765L-51.7218 80.6531L-51.6996 69.2612L-39.7145 69.2846L-39.7367 80.6765ZM-26.6618 68.4043L-38.2046 68.3818L-38.1811 56.366L-26.6384 56.3885L-26.6618 68.4043ZM-13.2626 54.8044L-25.3483 54.7808L-25.3245 42.5839L-13.2389 42.6074L-13.2626 54.8044ZM0.1957 41.8084L-12.1513 41.7843L-12.1256 28.5609L0.221468 28.585L0.1957 41.8084ZM13.1936 27.7649L1.10793 27.7414L1.13299 14.8802L13.2187 14.9038L13.1936 27.7649ZM26.8812 41.5786L14.7956 41.5551L14.8208 28.6134L26.9064 28.637L26.8812 41.5786ZM40.3893 54.6272L28.3037 54.6036L28.3274 42.4067L40.4131 42.4302L40.3893 54.6272ZM53.6756 67.9973L41.59 67.9738L41.6129 56.2196L53.6985 56.2431L53.6756 67.9973ZM41.9235 82.6068L54.0091 82.6303L53.9869 94.0222L41.9013 93.9987L41.9235 82.6068ZM28.5858 95.5829L40.6714 95.6064L40.6477 107.803L28.5621 107.78L28.5858 95.5829ZM1.2855 122.359L13.3712 122.383L13.3461 135.244L1.26044 135.22L1.2855 122.359ZM-11.9189 108.264L0.428099 108.289L0.402331 121.512L-11.9447 121.488L-11.9189 108.264ZM-25.0651 95.2167L-12.9795 95.2402L-13.0032 107.437L-25.0889 107.414L-25.0651 95.2167ZM-13.6548 80.6065L-25.117 80.5842L-25.0952 69.3936L-13.633 69.4159L-13.6548 80.6065ZM0.0237091 68.1544L-12.062 68.1308L-12.0391 56.3767L0.0466141 56.4002L0.0237091 68.1544ZM26.9902 68.2069L14.6431 68.1829L14.6665 56.1671L27.0136 56.1911L26.9902 68.2069ZM14.9549 93.9461L14.9766 82.8159L27.1025 82.8396C27.1025 82.8396 27.2433 82.7794 27.3238 82.7393L27.3019 93.9702L14.9549 93.9461ZM13.0302 80.6585L0.944527 80.635L0.966334 69.4444L13.052 69.4679L13.0302 80.6585ZM-11.7285 82.7639L0.357141 82.7874L0.335962 93.656L-11.7496 93.6324L-11.7285 82.7639ZM1.61985 95.2687L13.9669 95.2927L13.9426 107.751L1.59557 107.727L1.61985 95.2687ZM39.9962 80.9727L27.8703 80.9491C27.8703 80.9491 27.7294 81.0092 27.6489 81.0493L27.6719 69.2348L40.019 69.2588L39.9961 80.9929L39.9962 80.9727ZM13.6838 54.8368L1.33673 54.8127L1.36101 42.3541L13.708 42.3782L13.6838 54.8368ZM-37.8911 82.9947L-26.3485 83.0172L-26.3691 93.6241L-37.9118 93.6016L-37.8911 82.9947ZM27.0873 121.564L15.0017 121.54L15.0269 108.599L27.1125 108.622L27.0873 121.564ZM66.6811 81.0449L54.5954 81.0213L54.6183 69.2873L66.7039 69.3108L66.6811 81.0449ZM-65.8148 68.489C-66.1165 68.4884 -66.3583 68.7295 -66.3589 69.0314L-66.3853 82.5769C-66.3858 82.8788 -66.145 83.1208 -65.8433 83.1213L-52.531 83.1473L-52.5536 94.7203C-52.5542 95.0222 -52.3133 95.2642 -52.0117 95.2648L-39.7249 95.2887L-39.7491 107.667C-39.7496 107.969 -39.5088 108.211 -39.2072 108.211L-26.5786 108.236L-26.6044 121.479C-26.605 121.781 -26.3642 122.023 -26.0625 122.024L-13.3735 122.049L-13.3999 135.554C-13.4004 135.856 -13.1596 136.098 -12.858 136.098L0.19288 136.124L0.167229 149.287C0.166641 149.589 0.407526 149.831 0.709161 149.831L14.2426 149.858C14.5443 149.858 14.7861 149.617 14.7867 149.315L14.8123 136.152L27.3806 136.177C27.6822 136.177 27.924 135.936 27.9246 135.634L27.9504 122.391L41.1219 122.417C41.4236 122.417 41.6654 122.176 41.666 121.874L41.6918 108.631L54.6823 108.656C54.984 108.657 55.2257 108.415 55.2263 108.114L55.2506 95.6348L68.02 95.6597C68.3216 95.6603 68.5634 95.4192 68.564 95.1173L68.5883 82.6587L80.7142 82.6824C81.0158 82.6829 81.2576 82.4419 81.2582 82.14L81.2846 68.5945C81.2852 68.2926 81.0444 68.0506 80.7427 68.05L68.2548 68.0257L68.2798 55.2249C68.2804 54.923 68.0395 54.681 67.7379 54.6805L54.9685 54.6556L54.9929 42.1768C54.9934 41.8749 54.7526 41.6329 54.451 41.6324L41.4604 41.607L41.4862 28.3635C41.4868 28.0616 41.246 27.8196 40.9443 27.819L27.7728 27.7933L27.7986 14.5699C27.7991 14.268 27.5583 14.026 27.2566 14.0254L14.6884 14.0009L14.714 0.83787C14.7146 0.535965 14.4738 0.293974 14.1721 0.293386L0.638598 0.267013C0.336963 0.266426 0.0952052 0.507476 0.0946169 0.809381L0.0689663 13.9724L-12.9819 13.947C-13.2836 13.9464 -13.5254 14.1875 -13.526 14.4894L-13.5523 27.9946L-26.2412 27.9699C-26.5428 27.9693 -26.7846 28.2103 -26.7852 28.5122L-26.811 41.7558L-39.4396 41.7312C-39.7412 41.7306 -39.983 41.9716 -39.9836 42.2735L-40.0078 54.6516L-52.2945 54.6277C-52.5961 54.6271 -52.8379 54.8682 -52.8385 55.1701L-52.8645 68.5143L-65.8148 68.489Z'
          fill='#51504D'
        />
      </m.svg>
      <svg
        className='translate absolute -left-1.5 top-32 block -translate-x-2 sm:right-0 sm:top-36 md:hidden'
        xmlns='http://www.w3.org/2000/svg'
        width='41'
        height='40'
        viewBox='0 0 41 40'
        fill='none'
      >
        <path
          d='M26.3551 25.9839H14.1845V14.5667H26.3551V25.9839ZM0.657344 27.7433H13.3056V39.4857C13.3056 39.7726 13.5349 40.002 13.8214 40.002H26.7945C27.0811 40.002 27.3104 39.7726 27.3104 39.4857V26.8062H39.8249C40.1114 26.8062 40.3407 26.5767 40.3407 26.2899V13.2853C40.3407 12.9985 40.1114 12.769 39.8249 12.769H27.1766V1.02661C27.1766 0.739746 26.9473 0.510254 26.6607 0.510254H13.6877C13.4011 0.510254 13.1718 0.739746 13.1718 1.02661V13.7061H0.657344C0.370753 13.7061 0.141479 13.9356 0.141479 14.2224V27.2079C0.141479 27.4947 0.370753 27.7242 0.657344 27.7242'
          fill='#51504D'
        />
      </svg>

      {/* right */}
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        className='translate absolute left-0 top-32 hidden -translate-x-2 sm:left-auto sm:right-0 sm:top-36 sm:-translate-x-44 md:block'
        xmlns='http://www.w3.org/2000/svg'
        width='41'
        height='40'
        viewBox='0 0 41 40'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 176 - 10 - 176 : -176,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M26.3551 25.9839H14.1845V14.5667H26.3551V25.9839ZM0.657344 27.7433H13.3056V39.4857C13.3056 39.7726 13.5349 40.002 13.8214 40.002H26.7945C27.0811 40.002 27.3104 39.7726 27.3104 39.4857V26.8062H39.8249C40.1114 26.8062 40.3407 26.5767 40.3407 26.2899V13.2853C40.3407 12.9985 40.1114 12.769 39.8249 12.769H27.1766V1.02661C27.1766 0.739746 26.9473 0.510254 26.6607 0.510254H13.6877C13.4011 0.510254 13.1718 0.739746 13.1718 1.02661V13.7061H0.657344C0.370753 13.7061 0.141479 13.9356 0.141479 14.2224V27.2079C0.141479 27.4947 0.370753 27.7242 0.657344 27.7242'
          fill='#51504D'
        />
      </m.svg>
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        className='translate absolute right-0 top-16 hidden -translate-x-24 md:block'
        xmlns='http://www.w3.org/2000/svg'
        width='67'
        height='67'
        viewBox='0 0 67 67'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 96 - 10 - 96 : -96,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M26.8281 26.7718C27.1147 26.7718 27.344 26.5423 27.344 26.2555V14.1115H39.8967V26.2555C39.8967 26.5423 40.126 26.7718 40.4126 26.7718H52.1055V39.2791H39.9731C39.6865 39.2791 39.4573 39.5086 39.4573 39.7955V52.2455H27.6497V39.8529C27.6497 39.566 27.4204 39.3365 27.1338 39.3365H14.6766V26.7718H26.7899H26.8281ZM1.18774 40.0824H13.6449V52.8574C13.6449 53.1443 13.8742 53.3738 14.1608 53.3738H26.8281V65.7664C26.8281 66.0532 27.0574 66.2827 27.344 66.2827H40.3361C40.6227 66.2827 40.852 66.0532 40.852 65.7664V53.2973H52.9844C53.271 53.2973 53.5002 53.0678 53.5002 52.7809V40.0059H65.6326C65.9192 40.0059 66.1485 39.7764 66.1485 39.4895V26.5041C66.1485 26.2172 65.9192 25.9877 65.6326 25.9877H53.9397V13.27C53.9397 12.9832 53.7104 12.7537 53.4238 12.7537H40.6609V0.590576C40.6609 0.303711 40.4317 0.0742188 40.1451 0.0742188H27.172C26.8854 0.0742188 26.6562 0.303711 26.6562 0.590576V12.7154H13.8933C13.6067 12.7154 13.3774 12.9449 13.3774 13.2318V26.0068H1.18774C0.901148 26.0068 0.671875 26.2363 0.671875 26.5232V39.5086C0.671875 39.7955 0.901148 40.025 1.18774 40.025'
          fill='#51504D'
        />
      </m.svg>
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        className='absolute -right-12 top-36 hidden md:block'
        xmlns='http://www.w3.org/2000/svg'
        width='180'
        height='143'
        viewBox='-20 0 180 143'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 50 - 10 : 0,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M63.2249 54.2128C63.7025 54.2128 64.0847 53.8303 64.0847 53.3522V41.1891C64.0847 41.1891 64.0465 41.1509 64.0465 41.1126H76.0642V53.0845C76.0642 53.5626 76.4463 53.9642 76.9431 53.9642H88.7124V65.4579C88.7124 65.9361 89.1137 66.3377 89.5913 66.3377H100.979V77.4872H89.5913C89.1137 77.4872 88.7124 77.8697 88.7124 78.3669V89.8606H77.1341C76.6565 89.8606 76.2552 90.2431 76.2552 90.7403V102.884C76.2552 102.884 76.2935 102.942 76.2935 102.961H64.333C64.3904 102.846 64.4668 102.75 64.4668 102.636V90.4726C64.4668 89.9945 64.0847 89.5929 63.607 89.5929H52.0478V78.1756C52.0478 77.6975 51.6657 77.315 51.188 77.315H39.4569V66.6628C39.4569 66.6628 39.3996 66.548 39.3805 66.4907H50.9015C51.3791 66.4907 51.7612 66.1082 51.7612 65.6301V54.2128H63.244H63.2249ZM13.721 52.5873C13.2433 52.5873 12.8612 52.9697 12.8612 53.4478V65.6109C12.8612 65.6109 12.9185 65.7257 12.9376 65.7831H0.919896C0.442244 65.7831 0.0410156 66.1847 0.0410156 66.6628V78.8067C0.0410156 79.2849 0.423138 79.6865 0.919896 79.6865H13.2242V90.3387C13.2242 90.8168 13.6063 91.1993 14.084 91.1993H25.4139V102.617C25.4139 103.095 25.796 103.477 26.2737 103.477H38.2914C38.1768 103.649 38.043 103.841 38.043 104.051V115.717C38.043 116.329 38.5398 116.845 39.1512 116.845H50.5193C50.5193 116.845 50.4811 116.883 50.4811 116.922V129.085C50.4811 129.563 50.8632 129.964 51.36 129.964H63.3968V142.472C63.3968 142.759 63.6261 142.988 63.9127 142.988H76.7711C77.0577 142.988 77.287 142.759 77.287 142.472V129.964H88.9417C89.4194 129.964 89.8206 129.563 89.8206 129.085V116.922C89.8206 116.922 89.8015 116.883 89.8015 116.845H101.666C102.278 116.845 102.775 116.348 102.775 115.717V104.051C102.775 103.917 102.679 103.841 102.641 103.726H114.754C115.232 103.726 115.633 103.343 115.633 102.846V91.3523H127.421C127.899 91.3523 128.3 90.9507 128.3 90.4726V78.998H139.458C139.936 78.998 140.337 78.6155 140.337 78.1183V65.9552C140.337 65.4771 139.936 65.0755 139.458 65.0755H127.937V53.2566C127.937 52.7785 127.555 52.3769 127.058 52.3769H115.27V40.864C115.27 40.3859 114.888 40.0034 114.391 40.0034H103.099C103.099 40.0034 103.138 39.9461 103.138 39.9269V28.2802C103.138 27.6682 102.641 27.1519 102.01 27.1519H89.362C89.4194 27.0371 89.4958 26.9606 89.4958 26.8267V14.6637C89.4958 14.1856 89.0946 13.7839 88.6169 13.7839H76.924V1.33398C76.924 1.04712 76.6947 0.817627 76.4081 0.817627H63.5497C63.2631 0.817627 63.0338 1.04712 63.0338 1.33398V13.8413H50.997C50.5193 13.8413 50.1181 14.2238 50.1181 14.721V26.8841C50.1181 27.018 50.2136 27.0945 50.2519 27.2092H38.6544C38.0239 27.2092 37.5272 27.7065 37.5272 28.3376V39.9843C37.5272 40.1182 37.6227 40.1947 37.6609 40.3094H25.8725C25.3948 40.3094 24.9936 40.6919 24.9936 41.17V52.5873H13.6637H13.721Z'
          fill='#51504D'
        />
      </m.svg>
    </div>
  );
}
export default function ThreeRowTextButton(props: {
  data?: {
    sectionId?: string;
    heading?: PortableTextBlock;
    subHeading?: PortableTextBlock;
    button: {
      text: string;
      link: MenuItemLink;
    };
    displayOptions: {
      padding?: 'py' | 'pt' | 'pb' | 'p-0' | undefined;
    };
  };
}) {
  const sectionId = props.data?.sectionId;
  const heading = props.data?.heading;
  const subHeading = props.data?.subHeading;
  const section = useRef(null);

  const isInView = useInView(section, { amount: 0.4, once: true });

  const displayOptions = props.data?.displayOptions;
  const button = props.data?.button;

  const { coordinates: mousePosition, handleMouseMove } =
    useRelativeCoordinates(section);

  return (
    <>
      <section
        id={sectionId}
        ref={section}
        className={`${sectionPadding(displayOptions?.padding)} relative scroll-m-16`}
        onMouseMove={(e) => handleMouseMove(e)}
      >
        <m.div
          className={`sm:container-padding container relative mx-0 sm:mx-auto`}
          variants={fadeUp}
          initial='hidden'
          animate={isInView ? 'visible' : 'hidden'}
        >
          <div className='relative flex flex-col items-center justify-center gap-6 overflow-hidden rounded-3xl bg-[radial-gradient(121.58%_121.58%_at_50%_125.11%,#D3B203_0%,#141414_44.39%)] px-12 pb-16 pt-8 shadow-xl sm:gap-8 sm:rounded-[47px] sm:px-20 sm:pb-24 sm:pt-16 xs:px-16'>
            <SVGPatterns mousePosition={mousePosition} isInView={isInView} />
            {/* )} */}
            <m.div
              initial='hidden'
              animate={isInView ? 'visible' : 'hidden'}
              variants={fadeUp}
              className='sm:heading-xl relative z-10 max-w-sm text-center font-display text-[32px] leading-8 tracking-tighter text-charcoal sm:max-w-3xl'
            >
              {heading && (
                <PortableText
                  components={components}
                  value={heading}
                ></PortableText>
              )}
            </m.div>
            <m.div
              initial='hidden'
              animate={isInView ? 'visible' : 'hidden'}
              variants={fadeUp}
              className='heading-xs relative z-10 max-w-sm text-center font-sans text-charcoal sm:max-w-3xl'
            >
              {subHeading && (
                <PortableText
                  components={components}
                  value={subHeading}
                ></PortableText>
              )}
            </m.div>

            {/* button link */}
            {button?.text && button?.link && (
              <div className='-ml-4 mt-1 flex sm:ml-12 sm:mt-0'>
                <IKButton
                  color='transparent'
                  variant='filled'
                  className='group flex items-center gap-2 border border-[#757575] bg-[#282825b3] text-left text-[#EFE9DB] hover:bg-charcoal-800 hover:text-yellow-400 hover:ring-0'
                  link={button?.link}
                >
                  <span>{button?.text}</span>
                  <div className='flex h-7 w-7 items-center justify-center rounded-full bg-yellow text-black transition duration-500 group-hover:translate-x-2'>
                    <ArrowRight
                      weight='bold'
                      className='text-currentColor text-lg'
                    />
                  </div>
                </IKButton>
              </div>
            )}
          </div>
        </m.div>
      </section>
    </>
  );
}
